import React, { useEffect, useState } from 'react';
import {
  Button,
  Card,
  Col,
  FormField,
  Icon,
  Input,
  Row,
  Typography,
} from 'components/alf-design';
import { useForm, validate as externalValidator, useApi } from 'utils';
import './Initial.css';
import { datadogLogs } from '@datadog/browser-logs';
import { Guidance } from 'components/layout/login';

function validate(values) {
  const errors = {};

  if (!values?.lastName && values?.policyNumber) {
    errors.lastName = 'Required';
  }

  if (!values?.policyNumber) {
    errors.policyNumber = 'Policy number is invalid.';
  } else {
    const msgError = externalValidator.policyNumberPayment(
      values.policyNumber.trim().toUpperCase().replace(/ /g, ''),
    );
    if (msgError) {
      errors.policyNumber = msgError;
    }
  }

  return errors;
}

const Initial = ({ policyNumber, setStep, setPaymentData }) => {
  const api = useApi();
  const [error, setError] = useState(null);

  const {
    handleSubmit,
    handleChange,
    handleBlur,
    initForm,
    values,
    errors,
    touched,
  } = useForm(
    {
      policyNumber,
      lastName: '',
    },
    validate,
    true,
  );

  const getPaymentInfo = async (policyNumber, lastName) => {
    try {
      const res = await api.getPolicyPayments(policyNumber, lastName);
      if (res.data) {
        return res.data;
      }
      setError(
        'Policy not found or is not available for payment. Please verify the information and try again, or contact Slide Customer Service.',
      );
      return null;
    } catch (error) {
      setError(
        'Policy not found or is not available for payment. Please verify the information and try again, or contact Slide Customer Service.',
      );

      datadogLogs.logger.error(`Load Payment information - ${policyNumber}`, {
        error,
      });
      return null;
    }
  };

  const onSubmit = (e, values) => {
    handleSubmit(e, async () => {
      e.preventDefault();
      if (Object.keys(errors).length > 0) {
        return;
      }
      const data = await getPaymentInfo(
        values?.policyNumber.replace(/ /g, '').trim().toUpperCase(),
        values?.lastName,
      );
      if (data) {
        if (data?.billing === null) {
          setError(
            "We're currently experiencing technical difficulties. Please try again later.",
          );
          return;
        }
        setPaymentData(data);
        setStep('summary');
      }
    });
  };

  useEffect(() => {
    if (policyNumber !== values.policyNumber) {
      initForm({ policyNumber });
    }
  }, [policyNumber]);

  return (
    <Card className="initial">
      <Row className="row">
        <Typography className="subtitle">
          Let's get your current billing information.
        </Typography>
      </Row>
      {error && (
        <div className="policyError">
          <Icon name="timesCircleFull" color="#dc2a2a" fontSize={25} />
          <Typography className="policyError-text">{error}</Typography>
        </div>
      )}
      <div className="row inputs">
        <Col xs={23} className="inputLeft">
          <FormField
            label="Policy Number"
            error={touched.policyNumber ? errors?.policyNumber : ''}
            icon={<Guidance />}
          >
            <Input
              placeholder="Type policy number"
              name="policyNumber"
              id="policyNumber"
              onChange={handleChange}
              onBlur={handleBlur}
              value={values?.policyNumber}
              error={!!errors?.policyNumber && !!touched?.policyNumber}
              success={!errors?.policyNumber && !!touched?.policyNumber}
            />
          </FormField>
        </Col>
        <Col xs={23} className="inputRight">
          <FormField
            label="Policyholder Last Name"
            error={touched.lastName ? errors?.lastName : ''}
            divClassName="flex"
          >
            <Input
              placeholder="Type policyholder last name"
              name="lastName"
              id="lastName"
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.lastName}
              error={!!errors?.lastName && !!touched?.lastName}
              success={!errors?.lastName && !!touched?.lastName}
            />
          </FormField>
        </Col>
      </div>
      <Row className="row">
        <Typography className="subtitle">
          We accept online payments from your:
        </Typography>
        <ul>
          <li>
            <Typography>Credit Card</Typography>
          </li>
          <li>
            <Typography>Personal Checking Account</Typography>
          </li>
          <li>
            <Typography>Personal Savings Account</Typography>
          </li>
        </ul>
      </Row>
      <Row className="row customerService">
        If there are questions regarding this payment or if technical
        difficulties occur, please contact Slide Insurance Company Customer
        Service{' '}
        <a
          data-testid="customer-service"
          href={`tel:800-748-2030`}
          className="phone"
          tabIndex={0}
        >
          (800) 748-2030.
        </a>
      </Row>
      <Row className="row">
        <span className="span">
          Slide Insurance Company is pleased to offer you the convenience and
          ease of making secured online payments. Please be advised that making
          an online payment does not immediately change any disposition related
          to your account. Any payment made after normal business hours may not
          be posted until the next business day. We reserve the right to decline
          the acceptance of an online payment if your account is in default for
          any reason. Payments will be accepted from a credit card or personal
          checking or savings account.
        </span>
      </Row>
      <Row className="row">
        <Typography className="responsability">
          It is your responsibility to enter the correct routing/account
          information or card information. Any transactions declined due to data
          entry error will incur an insufficient funds processing fee of $25.
        </Typography>
      </Row>
      <Row className="row buttonRow">
        <Button
          id="continue-login"
          type="primary"
          className="continue-button"
          onClick={(e) => onSubmit(e, values)}
        >
          Continue
        </Button>
      </Row>
    </Card>
  );
};

export default Initial;
